import { supabase } from "@/platform/lib/supabase";

export type QuestionStatus = "draft" | "review" | "approved" | "rejected";
export type QuestionKind = "objective" | "discursive" | "essay";

export interface AdminQuestionRow {
  id: string;
  number: number | null;
  kind: QuestionKind;
  discipline: string;
  topic: string | null;
  difficulty: string | null;
  stem: string;
  alternatives: Array<{ label: string; text: string; is_correct?: boolean }> | null;
  correct_answer: string | null;
  has_base_text: boolean;
  base_text: string | null;
  status: QuestionStatus;
  reviewed_at: string | null;
  exam_year: number;
  exam_paper: string;
}

export interface AdminQuestionFilters {
  status?: QuestionStatus | "all";
  discipline?: string | "all";
  kind?: QuestionKind | "all";
  year?: number | "all";
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AdminQuestionPage {
  rows: AdminQuestionRow[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listAdminQuestions(
  f: AdminQuestionFilters = {},
): Promise<AdminQuestionPage> {
  const page = Math.max(1, f.page ?? 1);
  const pageSize = Math.min(100, Math.max(5, f.pageSize ?? 20));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from("einstein_questions")
    .select(
      "id, number, kind, discipline, topic, difficulty, stem, alternatives, correct_answer, has_base_text, base_text, status, reviewed_at, einstein_exam_papers!inner(year, paper)",
      { count: "exact" },
    )
    .order("status", { ascending: true })
    .order("discipline")
    .order("number", { ascending: true });

  if (f.status && f.status !== "all") q = q.eq("status", f.status);
  if (f.discipline && f.discipline !== "all") q = q.eq("discipline", f.discipline);
  if (f.kind && f.kind !== "all") q = q.eq("kind", f.kind);
  if (f.year && f.year !== "all") q = q.eq("einstein_exam_papers.year", f.year);
  if (f.search && f.search.trim().length > 0) {
    q = q.ilike("stem", `%${f.search.trim()}%`);
  }

  const { data, count, error } = await q.range(from, to);
  if (error) throw error;

  type Raw = Omit<AdminQuestionRow, "exam_year" | "exam_paper"> & {
    einstein_exam_papers: { year: number; paper: string };
  };
  const rows: AdminQuestionRow[] = (data as unknown as Raw[]).map((r) => ({
    id: r.id,
    number: r.number,
    kind: r.kind,
    discipline: r.discipline,
    topic: r.topic,
    difficulty: r.difficulty,
    stem: r.stem,
    alternatives: r.alternatives,
    correct_answer: r.correct_answer,
    has_base_text: r.has_base_text,
    base_text: r.base_text,
    status: r.status,
    reviewed_at: r.reviewed_at,
    exam_year: r.einstein_exam_papers.year,
    exam_paper: r.einstein_exam_papers.paper,
  }));

  return { rows, total: count ?? rows.length, page, pageSize };
}

export async function setQuestionStatus(
  id: string,
  status: QuestionStatus,
  reviewerId?: string,
): Promise<void> {
  const { error } = await supabase
    .from("einstein_questions")
    .update({
      status,
      reviewed_by: reviewerId ?? null,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function patchQuestion(
  id: string,
  patch: Partial<{
    stem: string;
    correct_answer: string | null;
    discipline: string;
    topic: string | null;
    difficulty: string | null;
    base_text: string | null;
  }>,
): Promise<void> {
  const { error } = await supabase
    .from("einstein_questions")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function getCorpusStats(): Promise<{
  byStatus: Record<string, number>;
  byDiscipline: Array<{ discipline: string; total: number; approved: number }>;
}> {
  const { data: byStatus } = await supabase.rpc("noop_stats" as never).select("*").maybeSingle();
  // RPC opcional não existe — fazemos via 2 selects simples.
  const { data: statusRows } = await supabase
    .from("einstein_questions")
    .select("status");
  const byStatusMap: Record<string, number> = {};
  for (const r of (statusRows ?? []) as Array<{ status: string }>) {
    byStatusMap[r.status] = (byStatusMap[r.status] ?? 0) + 1;
  }

  const { data: discRows } = await supabase
    .from("einstein_questions")
    .select("discipline, status");
  const byDiscMap: Record<string, { total: number; approved: number }> = {};
  for (const r of (discRows ?? []) as Array<{ discipline: string; status: string }>) {
    const b = byDiscMap[r.discipline] ?? { total: 0, approved: 0 };
    b.total += 1;
    if (r.status === "approved") b.approved += 1;
    byDiscMap[r.discipline] = b;
  }
  void byStatus;

  return {
    byStatus: byStatusMap,
    byDiscipline: Object.entries(byDiscMap).map(([discipline, v]) => ({
      discipline,
      ...v,
    })),
  };
}
